/**
 * Cart service for managing cart operations with diff-based updates
 */

import type {
  Cart,
  CartDiffResult,
  CartAddOperation,
  CartUpdateOperation,
  CartDeleteOperation,
  CartLookupItem,
  CartLineGroup,
  GridLookupItem,
} from "../types/cart.js";
import { fetchWithRetry } from "../utils/fetchWithRetry.js";

/** Default warehouse for lines that carry no `_warehouse_code` property. */
export const DEFAULT_WAREHOUSE_CODE = "WPBR";

/** Canonical line key shared by the grid, the cart lookup and cart sync. */
export function cartLineKey(
  variantId: string | number,
  warehouseCode: string | null | undefined,
): string {
  return `${variantId}|${warehouseCode || DEFAULT_WAREHOUSE_CODE}`;
}

/**
 * Group a cart's line items by canonical key.
 *
 * Unlike buildCartLookup this does NOT collapse duplicates — a key holding
 * more than one line item is real and must be repaired before any diff, or the
 * hidden line rides through to checkout. See CartLineGroup.
 */
export function groupCartLines(cart: Cart): Map<string, CartLineGroup> {
  const groups = new Map<string, CartLineGroup>();

  cart.items.forEach((item) => {
    const warehouseCode =
      item.properties?._warehouse_code || DEFAULT_WAREHOUSE_CODE;
    const key = cartLineKey(item.variant_id, warehouseCode);
    const existing = groups.get(key);

    if (existing) {
      groups.set(key, {
        key,
        variantId: existing.variantId,
        warehouseCode: existing.warehouseCode,
        totalQuantity: existing.totalQuantity + item.quantity,
        lineKeys: [...existing.lineKeys, item.key],
      });
      return;
    }

    groups.set(key, {
      key,
      variantId: item.variant_id,
      warehouseCode,
      totalQuantity: item.quantity,
      lineKeys: [item.key],
    });
  });

  return groups;
}

/**
 * Build cart lookup map using composite key.
 *
 * Exported for cart sync, which needs the same `_warehouse_code || 'WPBR'`
 * fallback the grid uses — duplicating it would let the two drift.
 */
export function buildCartLookup(cart: Cart): Map<string, CartLookupItem> {
  const lookup = new Map<string, CartLookupItem>();

  cart.items.forEach((item) => {
    const variantId = item.variant_id.toString();
    const warehouseCode = item.properties?._warehouse_code || "WPBR";
    const key = `${variantId}|${warehouseCode}`;

    lookup.set(key, {
      lineKey: item.key,
      quantity: item.quantity,
    });
  });

  return lookup;
}

/**
 * Build grid lookup map from page inputs.
 *
 * Reads variant identity (variant id, warehouse code) directly from each
 * quantity input's data-* attributes.
 */
function buildGridLookup(): Map<string, GridLookupItem> {
  const lookup = new Map<string, GridLookupItem>();

  $(".variant_qty_input_directship").each(function () {
    const input = this as HTMLInputElement;
    const quantity = parseInt(input.value) || 0;
    if (quantity === 0) return;

    const variantId = input.dataset.variantId;
    if (!variantId) return;
    const warehouseCode = input.dataset.warehouseCode || "WPBR";

    lookup.set(`${variantId}|${warehouseCode}`, {
      quantity,
    });
  });

  return lookup;
}

/** A whole-cart difference expressed in canonical keys, with no DOM involved. */
export interface CanonicalCartDiff {
  /** Keys present in `current` but not in `target`. */
  readonly toRemove: readonly string[];
  /** Keys whose quantity differs, with the quantity `target` wants. */
  readonly toSet: ReadonlyMap<string, number>;
}

/**
 * Diff two canonical `key → quantity` maps.
 *
 * Pure: no DOM, no jQuery, no page scoping. This is the differ cart sync uses
 * to apply a remote cart, because `calculateCartDiff` below deliberately
 * restricts itself to variants rendered on the current page and would silently
 * skip every remote change for a product that is not on screen.
 */
export function diffCarts(
  current: ReadonlyMap<string, number>,
  target: ReadonlyMap<string, number>,
): CanonicalCartDiff {
  const toRemove: string[] = [];
  const toSet = new Map<string, number>();

  current.forEach((_quantity, key) => {
    if (!target.has(key)) {
      toRemove.push(key);
    }
  });

  target.forEach((quantity, key) => {
    if (current.get(key) !== quantity) {
      toSet.set(key, quantity);
    }
  });

  return { toRemove, toSet };
}

/**
 * Calculate cart diff
 */
export function calculateCartDiff(
  cartLookup: Map<string, CartLookupItem>,
  gridLookup: Map<string, GridLookupItem>,
): CartDiffResult {
  const toDelete: CartDeleteOperation[] = [];
  const toUpdate: CartUpdateOperation[] = [];
  const toAdd: CartAddOperation[] = [];

  const pageVariantIds = new Set<string>();
  $(".variant_qty_input_directship").each(function () {
    const id = (this as HTMLInputElement).dataset.variantId;
    if (id) pageVariantIds.add(id);
  });

  cartLookup.forEach((cartItem, key) => {
    const [variantId, warehouseCode] = key.split("|");

    if (!pageVariantIds.has(variantId)) {
      return;
    }

    const gridItem = gridLookup.get(key);

    if (!gridItem || gridItem.quantity === 0) {
      toDelete.push({
        key,
        lineKey: cartItem.lineKey,
        variantId,
        warehouseCode,
        quantity: cartItem.quantity,
      });
    } else if (gridItem.quantity !== cartItem.quantity) {
      toUpdate.push({
        key,
        lineKey: cartItem.lineKey,
        variantId,
        warehouseCode,
        oldQty: cartItem.quantity,
        newQty: gridItem.quantity,
      });
    }
  });

  gridLookup.forEach((gridItem, key) => {
    if (!cartLookup.has(key) && gridItem.quantity > 0) {
      const [variantId, warehouseCode] = key.split("|");
      toAdd.push({
        key,
        variantId,
        warehouseCode,
        quantity: gridItem.quantity,
      });
    }
  });

  return { toDelete, toUpdate, toAdd };
}

/**
 * Execute cart operations.
 *
 * Deletes and updates are merged into a single /cart/update.js call keyed by
 * line item key — stable references that never shift as other lines change,
 * and atomic on Shopify's side so there is no intra-batch lock contention.
 * New items are then added via a single /cart/add.js call, appended to the
 * end of the cart in the same order as the current /cart/change.js flow.
 */
export async function executeCartOperations(
  diff: CartDiffResult,
): Promise<void> {
  const deleteCount = diff.toDelete.length;
  const updateCount = diff.toUpdate.length;
  const addCount = diff.toAdd.length;

  const operationLabel = `Cart update: ${deleteCount} removed, ${updateCount} updated, ${addCount} added`;

  if (deleteCount + updateCount + addCount === 0) {
    console.log(`${operationLabel} - nothing to do`);
    return;
  }

  if (deleteCount + updateCount > 0) {
    const updates: Record<string, number> = {};
    diff.toDelete.forEach((op) => {
      updates[op.lineKey] = 0;
    });
    diff.toUpdate.forEach((op) => {
      updates[op.lineKey] = op.newQty;
    });

    await fetchWithRetry(
      "/cart/update.js",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates }),
      },
      `${operationLabel} (update phase)`,
    );
  }

  if (addCount > 0) {
    const items = diff.toAdd.map((op) => ({
      id: op.variantId,
      quantity: op.quantity,
      properties: {
        _warehouse_code: op.warehouseCode,
      },
    }));

    await fetchWithRetry(
      "/cart/add.js",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      },
      `${operationLabel} (add phase)`,
    );
  }
}

/** Abort a hung /cart.js read rather than block a sync cycle indefinitely. */
const CART_FETCH_TIMEOUT_MS = 10000;

/**
 * Fetch current cart.
 *
 * Checks `response.ok` and enforces a timeout. Both matter for cart sync: an
 * error page parsed as JSON throws, and a caller that swallowed the throw would
 * see an empty cart — which the sync decision procedure could read as "the
 * customer emptied their cart" and propagate. Absence of information must never
 * look like an empty cart.
 */
export async function fetchCart(): Promise<Cart> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CART_FETCH_TIMEOUT_MS);

  try {
    const response = await fetch("/cart.js", {
      cache: "no-store",
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`/cart.js returned HTTP ${response.status}`);
    }

    const data = (await response.json()) as Cart;

    if (!data || !Array.isArray(data.items)) {
      throw new Error("/cart.js returned an unexpected body");
    }

    return data;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Snapshot current quantity input values, keyed by `${variantId}|${warehouseCode}`.
 *
 * Returned as strings (raw trimmed input value) so the shape matches
 * dirtyTracking's baseline format — callers set baselines from this snapshot
 * after a successful cart update, representing "what we actually submitted"
 * rather than "whatever the DOM shows right now" (which may include edits
 * made during the in-flight network call).
 */
export function snapshotQuantityValues(): Map<string, string> {
  const snapshot = new Map<string, string>();
  $(".variant_qty_input_directship").each(function () {
    const input = this as HTMLInputElement;
    const variantId = input.dataset.variantId;
    if (!variantId) return;
    const warehouseCode = input.dataset.warehouseCode || "WPBR";
    snapshot.set(`${variantId}|${warehouseCode}`, (input.value || "").trim());
  });
  return snapshot;
}

/** What a grid submit actually did, for callers that need more than the snapshot. */
export interface CartUpdateResult {
  /**
   * Grid snapshot used to build the diff. Callers set dirty-tracking baselines
   * from it so edits made *during* the network I/O remain dirty and trigger a
   * rerun of the mobile auto-submit loop.
   */
  readonly snapshot: Map<string, string>;
  /** The exact changes submitted — cart sync turns these straight into ops. */
  readonly diff: CartDiffResult;
}

/**
 * Main cart update function.
 *
 * Returns both the grid snapshot and the diff that was executed. The diff is
 * the push payload cart sync needs; re-deriving it afterwards would double-fetch
 * and could miss a line the server clamped in between.
 */
export async function updateCart(): Promise<CartUpdateResult> {
  const cart = await fetchCart();
  const cartLookup = buildCartLookup(cart);
  const submittedSnapshot = snapshotQuantityValues();
  const gridLookup = buildGridLookup();
  const diff = calculateCartDiff(cartLookup, gridLookup);

  await executeCartOperations(diff);

  return { snapshot: submittedSnapshot, diff };
}
